import {
	ContextMenu,
	ContextMenuAnchorItem,
	ContextMenuDeleteWithModelUsageItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	FeedbackCreateDrawer,
	FeedbackIndexResource,
	FeedbackUpdateDrawer,
	IDrawerDataListColumn,
	PaginatedLaravelDrawerAutoDataList,
	defaultFeedbackService,
	useDynamicConfig,
	useFeedbackCrud,
	useHasPermission,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

export const AdminFeedbackList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { frontendUrl } = useDynamicConfig();
	const canCreate = useHasPermission(["feedback.create"]);
	const canUpdate = useHasPermission(["feedback.update"]);
	const canDelete = useHasPermission(["feedback.delete"]);
	const crudOperations = useFeedbackCrud(defaultFeedbackService);
	const showUsageAction = (id: string | number) => () => defaultFeedbackService.usages(id).then((r) => r.data.data);

	const dataListColumns: IDrawerDataListColumn<FeedbackIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuLinkItem
							label={t("Show feedback")}
							title={t("Show feedback")}
							shortcut="primary"
							icon="eye"
							to={`${id}/details`}
						/>
						{canUpdate && (
							<ContextMenuItem
								label={t("Edit feedback")}
								action={openDrawer}
								title={t("Edit feedback")}
								icon="edit"
								shortcut="default"
							/>
						)}
						<ContextMenuAnchorItem
							label={t("Show preview")}
							title={t("Show preview")}
							icon="arrow_outward"
							href={`${frontendUrl}/feedbacks/${id}/preview`}
							target="_blank"
							rel="noreferrer"
							shortcut="default"
						/>
						{canDelete && (
							<ContextMenuDeleteWithModelUsageItem
								label={t("Delete feedback")}
								title={t("Delete feedback")}
								showUsageAction={showUsageAction(id)}
								modelName={t("Feedback")}
								confirmationBody={t("Are you sure you want to delete this feedback?")}
								deleteAction={() => crudOperations.deleteEntity(id)}
							/>
						)}
					</ContextMenu>
				),
			},
		],
		[canDelete, canUpdate, crudOperations, frontendUrl, t],
	);

	return (
		<PaginatedLaravelDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			service={defaultFeedbackService}
			onRowClick={(feedback) => navigate(`${feedback.id}/details`)}
			renderCreateDrawer={(drawerProps) => (
				<FeedbackCreateDrawer {...drawerProps} onSubmit={crudOperations.createEntity} />
			)}
			renderUpdateDrawer={(drawerProps) => (
				<FeedbackUpdateDrawer
					{...drawerProps}
					service={defaultFeedbackService}
					onSubmit={(formValues) => crudOperations.updateEntity(drawerProps.id, formValues)}
				/>
			)}
			translations={{
				title: t("Feedback"),
				empty: t("No feedback available."),
			}}
			initialSorting={{ column: "name", direction: "asc" }}
			initialSearch={{ search: "", searchFields: [{ name: "feedback_translations.name", label: t("Name") }] }}
		/>
	);
};
